import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  getDirectionIcon,
  getDirectionIconColor
} from '@satellite/composables/loadTypePresenterShared';
import { translateLoadTypeDirection } from '@satellite/composables/useLoadTypeDisplay';

import { buildSelectGroupEntries } from '@/composables/selectGroupEntries';

import type { MaybeRefOrGetter } from 'vue';
import type { DockDetails } from '@satellite/composables/dockPresenterShared';
import type { LoadTypeDetails } from '@satellite/composables/loadTypePresenterShared';
import type { SelectGroupEntry } from '@/composables/selectGroupEntries';

export type LoadTypeSelectEntry = SelectGroupEntry<LoadTypeDetails>;

export interface UseLoadTypeSelectItemsOptions {
  loadTypes: MaybeRefOrGetter<readonly LoadTypeDetails[]>;
  /** Labels the warehouse group; org-level load types get their own group below it. */
  warehouseName: MaybeRefOrGetter<string>;
  /**
   * Narrows the rows to load types these docks offer. A dock whose `loadTypeIds` was
   * never fetched carries no information and is skipped, so a lean dock join widens the
   * list instead of emptying it; a dock with an explicit empty array narrows to nothing.
   */
  docks?: MaybeRefOrGetter<ReadonlyArray<Pick<DockDetails, 'loadTypeIds'>> | undefined>;
}

/**
 * Turns a flat load type list into the rows a select renders: dock narrowing, the
 * warehouse/org grouping, and the row copy. Kept out of the SFC so the shaping is
 * unit-testable without mounting Vuetify.
 */
export function useLoadTypeSelectItems(options: UseLoadTypeSelectItemsOptions) {
  const { t } = useI18n();

  const getName = (loadType: LoadTypeDetails) => loadType.name ?? '';

  const getDuration = (loadType: LoadTypeDetails) =>
    loadType.duration_min
      ? t('forms.elements.selects.loadTypeSelect.messages.nMinutes', { n: loadType.duration_min })
      : '';

  const getDirection = (loadType: LoadTypeDetails) =>
    translateLoadTypeDirection(t, loadType.direction) ?? '';

  const getLabel = (loadType: LoadTypeDetails) => getName(loadType);

  /** `null` means "no dock narrowing asked for", which differs from an empty allow-list. */
  const allowedLoadTypeIds = computed(() => {
    const docksWithKnownLoadTypes = (toValue(options.docks) ?? []).filter(dock =>
      Array.isArray(dock.loadTypeIds)
    );

    if (!docksWithKnownLoadTypes.length) {
      return null;
    }

    return new Set(docksWithKnownLoadTypes.flatMap(dock => dock.loadTypeIds));
  });

  /** The selectable rows, before grouping — what "select all" and the summary count on. */
  const visibleLoadTypes = computed(() => {
    const loadTypes = [...toValue(options.loadTypes)];
    const allowedIds = allowedLoadTypeIds.value;

    return allowedIds ? loadTypes.filter(loadType => allowedIds.has(loadType.id)) : loadTypes;
  });

  const entries = computed<LoadTypeSelectEntry[]>(() => {
    const warehouseGroupHeader = `${toValue(options.warehouseName)} ${t('global.specialtyTerms.loadTypes', 2)}`;

    return buildSelectGroupEntries(
      [
        {
          header: warehouseGroupHeader,
          items: visibleLoadTypes.value.filter(loadType => Boolean(loadType.warehouseId))
        },
        {
          header: t('global.specialtyTerms.orgLoadTypes'),
          items: visibleLoadTypes.value.filter(loadType => !loadType.warehouseId)
        }
      ],
      'load-type'
    );
  });

  return {
    entries,
    getDirection,
    getDirectionIcon,
    getDirectionIconColor,
    getDuration,
    getLabel,
    getName,
    visibleLoadTypes
  };
}
